/**
 * Appraisal delivery state contract — the ONE authority on what a queue row
 * means, what stage it reached, whether a human is needed, and what to tell them.
 *
 * Deliberately import-free so it can be consumed from three runtimes:
 *   - Deno edge functions (the exception dispatcher)
 *   - the React portal (the Appraisal Delivery report)
 *   - Node/Vitest (unit coverage)
 * Same precedent as `notification-recipient-contract.ts`, which `src/` already
 * imports directly. Do NOT add imports and do NOT fork a client-side copy: this
 * file replaced four hand-maintained crosswalks that had already diverged.
 *
 * The rule that matters most, learned from a production alert storm on
 * 2026-08-28: a document we have not read is a document we cannot classify.
 * "OCR has not finished" is pipeline latency, not a delivery exception, and it
 * must never page a person — until it has been stuck long enough to be real.
 */

export type AppraisalPipeline = 'cda_order' | 'cda_delivery' | 'transferred' | 'avm' | 'appraisal_audit';

/** Lane stops, in order. A row's furthest reached stop is its position. */
export type AppraisalStage =
  | 'uploaded'
  | 'read'
  | 'checked'
  | 'sent'
  | 'receipt'
  | 'recorded';

/** What an operator should do about this row, and nothing finer. */
export type AppraisalLifecycleState =
  | 'needs_action'
  | 'in_flight'
  | 'delivered'
  | 'excluded';

export interface AppraisalQueueSnapshot {
  pipeline: AppraisalPipeline;
  status: string;
  skipReason?: string | null;
  emailSentAt?: string | null;
  receiptNextRetryAt?: string | null;
  receiptLastError?: string | null;
  deliveryReceiptAttachmentId?: string | null;
  field3858WrittenAt?: string | null;
  field3858GaveUpAt?: string | null;
  /** ISO timestamp the row entered the queue. Drives the OCR staleness rule. */
  queuedAt: string;
  findingsOnly?: boolean;
  reviewMessage?: string | null;
}

export interface AppraisalDeliveryState {
  state: AppraisalLifecycleState;
  /** Furthest lane stop reached. */
  stage: AppraisalStage;
  /** Where progress is waiting or failed; null when no active stop applies. */
  stoppedAt: AppraisalStage | null;
  /** Stable machine key. Safe for grouping, metrics, and tests. */
  reason: string;
  /** Plain language, operator-facing. What happened. */
  headline: string;
  /** What the person should do. Null when there is nothing for them to do. */
  guidance: string | null;
  /** May this row generate an operator email? */
  alertable: boolean;
}

export const APPRAISAL_PIPELINE_LABELS: Record<AppraisalPipeline, string> = {
  cda_order: 'CDA ordering',
  cda_delivery: 'CDA to borrower',
  transferred: 'Transferred appraisal',
  avm: 'AVM to borrower',
  appraisal_audit: 'Appraisal audit'
};

/** Short phrase naming what the pipeline actually sends, for email copy. */
export const APPRAISAL_PIPELINE_ERRAND: Record<AppraisalPipeline, string> = {
  cda_order: 'a reminder to the processor to order the CDA',
  cda_delivery: 'the CDA to the borrower',
  transferred: 'the transferred appraisal to the borrower',
  avm: 'the AVM to the borrower',
  appraisal_audit: 'the appraisal audit and 1004D follow-up'
};

export const APPRAISAL_STATE_LABELS: Record<AppraisalLifecycleState, string> = {
  needs_action: 'Needs you',
  in_flight: 'In flight',
  delivered: 'Delivered',
  excluded: 'Not applicable'
};

/** Canonical persisted reason for a queue row whose source attachment is gone. */
export const APPRAISAL_ATTACHMENT_NOT_FOUND_REASON = 'attachment_not_found';

export const APPRAISAL_STAGE_LABELS: Record<AppraisalStage, string> = {
  uploaded: 'Uploaded',
  read: 'Read',
  checked: 'Checked',
  sent: 'Sent',
  receipt: 'Receipt',
  recorded: 'Recorded'
};

const FULL_LANE: readonly AppraisalStage[] = [
  'uploaded',
  'read',
  'checked',
  'sent',
  'receipt',
  'recorded'
];

/**
 * The lane a pipeline actually runs. CDA ordering emails a processor and stops —
 * it files no Encompass receipt and stamps no field 3858, so those stops are not
 * "N/A columns", they simply are not part of its lane.
 */
export function appraisalLaneFor(pipeline: AppraisalPipeline): readonly AppraisalStage[] {
  if (pipeline === 'cda_order' || pipeline === 'appraisal_audit') return ['uploaded', 'read', 'checked', 'sent'];
  if (pipeline === 'transferred') return ['uploaded', 'read', 'checked', 'sent', 'receipt'];
  return FULL_LANE;
}

/** Hours a row may sit unread before "waiting on OCR" becomes a real problem. */
export const OCR_STALL_HOURS = 24;

/**
 * Every queue status this file can resolve to `alertable: true`.
 *
 * The dispatcher MUST bound its candidate read with this list and nothing
 * narrower. It previously kept its own hand-written list that omitted
 * `invalid_document`, which is exactly what AVM writes when OCR runs out of
 * retries — so the report could show "Needs you" for a row no email could ever
 * be selected for. Add a status here in the same edit that makes it alertable.
 *
 * `sent` is absent on purpose: the only alert reachable from a fully sent row
 * is a field-3858 give-up, which the dispatcher selects by its own column.
 */
export const APPRAISAL_ALERTABLE_STATUSES: readonly string[] = [
  'pending_extraction',
  'needs_manual_review',
  'failed',
  'invalid_document',
  'skipped_missing_processor',
  'sent_no_receipt'
];

/** Reasons that are policy decisions, not failures. They never page anyone. */
const POLICY_SKIP_REASONS = new Set(['ineligible_loan_state', 'historical_loan_suppressed']);

/** Skip reasons meaning "we never finished reading the file". */
const UNREAD_SKIP_REASONS = new Set([
  'extraction_failed_after_retries',
  'extraction_unavailable',
  'source_extraction_skipped',
  'rotation_source_missing',
  'rotation_source_invalid'
]);

/**
 * CDA ordering reminds a processor; it never sends anything to a borrower and
 * has no ROV form in play. Shared guidance that says "send it to the borrower
 * with the ROV form" is simply wrong instruction for that pipeline.
 */
function guidanceFor(
  pipeline: AppraisalPipeline,
  borrower: string,
  ordering: string
): string {
  return pipeline === 'cda_order' ? ordering : borrower;
}

function isReceiptAbandoned(row: AppraisalQueueSnapshot): boolean {
  return row.status === 'sent_no_receipt'
    && (row.receiptNextRetryAt ?? null) === null
    && (row.receiptLastError || '').startsWith('receipt_abandoned');
}

function hoursSince(iso: string, now: Date): number {
  const started = Date.parse(iso);
  if (Number.isNaN(started)) return 0;
  return (now.getTime() - started) / 3_600_000;
}

function normalizeUnreadReason(skipReason: string | null | undefined): string | null {
  const reason = skipReason || '';
  if (!reason) return null;
  if (UNREAD_SKIP_REASONS.has(reason)) return reason;
  // Legacy free-text: "Extraction did not complete after 6 retries (status: pending)"
  if (/extraction.*did not complete/i.test(reason)) return 'extraction_failed_after_retries';
  return null;
}

function normalizeMissingAttachmentReason(
  skipReason: string | null | undefined
): typeof APPRAISAL_ATTACHMENT_NOT_FOUND_REASON | null {
  const reason = (skipReason || '').trim();
  if (reason === APPRAISAL_ATTACHMENT_NOT_FOUND_REASON) {
    return APPRAISAL_ATTACHMENT_NOT_FOUND_REASON;
  }
  // Legacy CDA-order rows written before the producer adopted the contract.
  if (/^attachment not found in encompass_attachments$/i.test(reason)) {
    return APPRAISAL_ATTACHMENT_NOT_FOUND_REASON;
  }
  return null;
}

/**
 * Resolve one queue row to its operator-facing state.
 *
 * Order matters and is the contract:
 *   1. sent-and-finished wins over everything (the borrower has the document)
 *   2. post-send bookkeeping failures are real, but never "the email failed"
 *   3. unread files are latency until they are stale, then they are a problem
 *   4. policy skips and content rejections are excluded, never alerts
 */
export function resolveAppraisalDeliveryState(
  row: AppraisalQueueSnapshot,
  now: Date = new Date()
): AppraisalDeliveryState {
  if (row.pipeline === 'appraisal_audit') {
    const unread = row.status === 'pending_extraction';
    const needsAction = ['needs_manual_review', 'failed'].includes(row.status) || (unread && hoursSince(row.queuedAt, now) >= OCR_STALL_HOURS);
    const excluded = row.status.startsWith('skipped_') || row.status === 'handled_manually' || POLICY_SKIP_REASONS.has(row.skipReason || '');
    const waiting = ['pending_extraction', 'waiting_xml'].includes(row.status);
    return {
      state: excluded ? 'excluded' : needsAction ? 'needs_action' : waiting ? 'in_flight' : 'delivered',
      stage: unread ? 'uploaded' : row.emailSentAt ? 'sent' : 'checked',
      stoppedAt: needsAction ? unread ? 'read' : 'checked' : null,
      reason: row.skipReason || (needsAction ? 'appraisal_review_required' : waiting ? 'appraisal_waiting' : 'appraisal_assessed'),
      headline: row.reviewMessage || (needsAction ? 'The appraisal audit needs review.' : excluded ? 'No automated follow-up is needed.' : waiting ? 'Waiting for the appraisal source.' : 'The appraisal audit is recorded.'),
      guidance: needsAction ? 'Open the Appraisal tab in Loan Center to review the report and its audit results.' : null,
      alertable: needsAction && !excluded && row.findingsOnly === false
    };
  }
  const lane = appraisalLaneFor(row.pipeline);
  const laneEnd = lane[lane.length - 1];
  const sent = Boolean(row.emailSentAt) || row.status === 'sent' || row.status === 'sent_no_receipt';

  // ---- 2. Post-send bookkeeping ------------------------------------------
  // The document reached the recipient. Anything below is filing, not delivery,
  // and the copy must never imply an email failed.
  if (sent) {
    if (lane.includes('recorded') && row.field3858GaveUpAt) {
      return {
        state: 'needs_action',
        stage: 'receipt',
        stoppedAt: 'recorded',
        reason: 'field_3858_gave_up',
        headline: 'Delivered, but the delivery date never reached Encompass.',
        guidance: 'Enter the delivery date in Encompass field 3858 yourself.',
        alertable: true
      };
    }
    if (isReceiptAbandoned(row)) {
      return {
        state: 'needs_action',
        stage: 'sent',
        stoppedAt: 'receipt',
        reason: 'receipt_abandoned',
        headline: 'Delivered, but the receipt never filed to Encompass.',
        guidance: 'Upload the delivery receipt to the Appraisal Delivery folder yourself.',
        alertable: true
      };
    }
    if (row.status === 'sent_no_receipt') {
      return {
        state: 'in_flight',
        stage: 'sent',
        stoppedAt: 'receipt',
        reason: 'receipt_retrying',
        headline: 'Delivered. Filing the receipt to Encompass.',
        guidance: null,
        alertable: false
      };
    }
    if (lane.includes('recorded') && !row.field3858WrittenAt) {
      return {
        state: 'in_flight',
        stage: 'receipt',
        stoppedAt: 'recorded',
        reason: 'field_3858_retrying',
        headline: 'Delivered. Writing the delivery date to Encompass.',
        guidance: null,
        alertable: false
      };
    }
    return {
      state: 'delivered',
      stage: laneEnd,
      stoppedAt: null,
      reason: 'delivered',
      headline: row.pipeline === 'cda_order'
        ? 'Reminder sent to the processor.'
        : 'Delivered to the borrower.',
      guidance: null,
      alertable: false
    };
  }

  // ---- 4a. Policy skips ---------------------------------------------------
  if (POLICY_SKIP_REASONS.has(row.skipReason || '')) {
    return {
      state: 'excluded',
      stage: 'checked',
      stoppedAt: null,
      reason: row.skipReason || 'policy_skip',
      headline: row.skipReason === 'historical_loan_suppressed'
        ? 'Held back — historical or paid-off loan.'
        : 'Held back — the loan is not in a state that sends to borrowers.',
      guidance: null,
      alertable: false
    };
  }

  // ---- 3. Unread files ----------------------------------------------------
  // We have not read this document, so we do not know what it is. Claiming an
  // appraisal email failed would be a guess. Latency until it is stale.
  const unreadReason = normalizeUnreadReason(row.skipReason);
  if (row.status === 'pending_extraction' || unreadReason) {
    const waitedHours = hoursSince(row.queuedAt, now);
    const lineageBroken = unreadReason === 'rotation_source_missing'
      || unreadReason === 'rotation_source_invalid';

    if (lineageBroken) {
      return {
        state: 'needs_action',
        stage: 'uploaded',
        stoppedAt: 'read',
        reason: unreadReason as string,
        headline: 'This file cannot be matched back to its original upload.',
        guidance: guidanceFor(
          row.pipeline,
          'Send the document to the borrower yourself, with the ROV form.',
          'Open the file in Encompass and order the CDA yourself if it is still needed.'
        ),
        alertable: true
      };
    }

    if (waitedHours >= OCR_STALL_HOURS) {
      return {
        state: 'needs_action',
        stage: 'uploaded',
        stoppedAt: 'read',
        reason: 'ocr_stalled',
        headline: `Still unreadable after ${OCR_STALL_HOURS} hours, so we cannot tell what this document is.`,
        guidance: 'Open the file in Encompass. If it is an appraisal or CDA, handle it yourself; otherwise no action is needed.',
        alertable: true
      };
    }

    return {
      state: 'in_flight',
      stage: 'uploaded',
      stoppedAt: 'read',
      reason: 'awaiting_ocr',
      headline: 'Waiting on the document text before we can tell what this is.',
      guidance: null,
      alertable: false
    };
  }

  // ---- 4b. Content decisions ---------------------------------------------
  // A missing attachment is NOT a content decision: we never read it, because
  // it is gone. Processors write the shared attachment-not-found reason, so
  // this must be checked before the generic content branch below, which would
  // otherwise claim we read and checked a file that no longer exists.
  //
  // Deliberately NOT alertable: there is no document to send and nothing a
  // person can do with a deleted file. Making it alertable would also fire a
  // catch-up alert for every historical row at once.
  const missingAttachmentReason = normalizeMissingAttachmentReason(row.skipReason);
  if (missingAttachmentReason) {
    return {
      state: 'excluded',
      stage: 'uploaded',
      stoppedAt: null,
      reason: missingAttachmentReason,
      headline: 'The file is no longer on the loan, so there is nothing to send.',
      guidance: null,
      alertable: false
    };
  }

  // We read it and it is not something this pipeline sends. Routine, not a fault.
  if (row.status === 'invalid_document' || row.status === 'skipped_invalid_document') {
    return {
      state: 'excluded',
      stage: 'checked',
      stoppedAt: null,
      reason: 'not_a_deliverable_document',
      headline: 'Read and checked — this is not a document this pipeline sends.',
      guidance: null,
      alertable: false
    };
  }
  if (row.status === 'skipped_duplicate' || row.status === 'skipped_duplicate_open_cda') {
    return {
      state: 'excluded',
      stage: 'checked',
      stoppedAt: null,
      reason: 'already_handled',
      headline: 'Another file on this loan already covered it.',
      guidance: null,
      alertable: false
    };
  }
  if (row.status === 'skipped_no_open_cda') {
    return {
      state: 'excluded',
      stage: 'checked',
      stoppedAt: null,
      reason: 'no_open_cda',
      headline: 'No open CDA condition on the loan, so no reminder was needed.',
      guidance: null,
      alertable: false
    };
  }
  if (row.status === 'skipped_missing_processor') {
    return {
      state: 'needs_action',
      stage: 'checked',
      stoppedAt: 'sent',
      reason: 'no_processor',
      headline: 'No processor is assigned, so there was nobody to remind.',
      guidance: 'Assign a processor in Encompass, then order the CDA if it is still needed.',
      alertable: true
    };
  }

  // ---- Terminal delivery failures ----------------------------------------
  const orderingFallback = 'Open the file in Encompass and order the CDA yourself if it is still needed.';
  const failures: Record<string, { headline: string; guidance: string }> = {
    no_borrower_email: {
      headline: 'There is no borrower email on the loan.',
      guidance: "Add the borrower's email in Encompass, then send the document yourself. This one will not retry."
    },
    required_attachment_unavailable_after_retries: {
      headline: 'The PDF or the ROV form could not be downloaded.',
      guidance: guidanceFor(
        row.pipeline,
        'Send the document to the borrower yourself, with the ROV form.',
        orderingFallback
      )
    },
    link_unavailable_after_retries: {
      headline: 'The secure download link could not be created.',
      guidance: guidanceFor(
        row.pipeline,
        'Send the document to the borrower yourself, with the ROV form.',
        orderingFallback
      )
    },
    pdf_too_large: {
      headline: 'The file is over 25 MB and secure-link delivery is off.',
      guidance: 'Send it yourself, or turn on secure-link delivery — it will then retry on its own.'
    },
    idempotency_payload_drift: {
      headline: 'The send was blocked to avoid a mismatched duplicate.',
      guidance: 'Check whether the original email arrived, then send it yourself if it did not.'
    }
  };
  const failure = failures[row.skipReason || ''];
  if (failure) {
    return {
      state: 'needs_action',
      stage: 'checked',
      stoppedAt: 'sent',
      reason: row.skipReason as string,
      headline: failure.headline,
      guidance: failure.guidance,
      alertable: true
    };
  }

  if (row.status === 'pending') {
    // The webhook creates `pending` BEFORE OCR and before content validation,
    // and recovery reuses the same status, so the snapshot cannot prove Read or
    // Checked happened. Claim only what is certain: the file was uploaded.
    return {
      state: 'in_flight',
      stage: 'uploaded',
      stoppedAt: null,
      reason: 'queued',
      headline: 'Queued. Waiting its turn to be read and checked.',
      guidance: null,
      alertable: false
    };
  }

  if (row.status === 'failed' || row.status === 'needs_manual_review') {
    return {
      state: 'needs_action',
      stage: 'checked',
      stoppedAt: 'sent',
      reason: row.skipReason || 'unknown_failure',
      headline: row.skipReason
        ? `Processing stopped: ${row.skipReason.replace(/_/g, ' ')}.`
        : 'Processing stopped for a reason we do not have copy for yet.',
      guidance: guidanceFor(
        row.pipeline,
        'Open the document in Encompass and send it yourself if it should have gone out.',
        'Open the document in Encompass and order the CDA yourself if it is still needed.'
      ),
      alertable: true
    };
  }

  return {
    state: 'excluded',
    stage: 'checked',
    stoppedAt: null,
    reason: row.status || 'unknown',
    headline: `Unrecognized state: ${row.status}.`,
    guidance: null,
    alertable: false
  };
}

/** Convenience: may this row page a human right now? */
export function isAlertableAppraisalRow(
  row: AppraisalQueueSnapshot,
  now: Date = new Date()
): boolean {
  return resolveAppraisalDeliveryState(row, now).alertable;
}
